import { ExitToApp } from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';
import React from 'react';
import { DocumentPath } from '../DocumentationButton/types';

import { useButtonStyles } from './styles';
import { useAllPrivileges, useContextMenu, useOrganizationWidePreference } from 'kineticUtils';
import { PREFERENCE_KEYS } from 'types/preferenceTypes';
import { KineticModule, PrivilegeType } from '@kinetic-ui/data-layer';
import { AdminMenu } from './components/AdminMenu';
import { ExternalLinksPreference } from './types';
import { ExternalLinkProvider } from './context';

const ExitToAppButton = ({
  onClick,
  tooltip,
}: {
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  tooltip: string | null;
}) => {
  const classes = useButtonStyles();
  return (
    <Tooltip title={tooltip}>
      <IconButton
        id='external-link-button'
        style={{ fontSize: 'inherit' }}
        onClick={onClick}
        className={classes.button}
      >
        <ExitToApp fontSize='small' />
      </IconButton>
    </Tooltip>
  );
};

const ExternalLinkButton = ({
  documentPath,
}: {
  documentPath: keyof typeof DocumentPath | undefined;
}) => {
  const { anchorEl, handleOpenMenu, handleCloseMenu } = useContextMenu();
  const isAdmin = useAllPrivileges([PrivilegeType.Update], KineticModule.System);
  const preferenceKey = documentPath;

  const { value: preference, setPreference } =
    useOrganizationWidePreference<ExternalLinksPreference>({ key: PREFERENCE_KEYS.EXTERNAL_LINKS });

  // If no preference key is available, don't show button
  if (!preferenceKey) {
    console.warn('No preference key available for external link button');
    return null;
  }

  const url = preference?.[preferenceKey]?.[0]?.url ?? null;
  const tooltip = preference?.[preferenceKey]?.[0]?.tooltip ?? null;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (isAdmin) {
      handleOpenMenu(event);
    } else if (url) {
      window.open(url);
    }
  };

  // If no external link is provided, don't show button / unless user is admin
  if (!url && !isAdmin) {
    return null;
  }

  return (
    <ExternalLinkProvider value={{ preference, setPreference, preferenceKey, url, tooltip }}>
      <ExitToAppButton onClick={handleClick} tooltip={tooltip} />
      <AdminMenu anchorEl={anchorEl} onClose={handleCloseMenu} url={url} />
    </ExternalLinkProvider>
  );
};

export default ExternalLinkButton;
